// @angular
import { Component, DestroyRef, OnDestroy, OnInit, ViewEncapsulation, effect, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

// @rxjs
import { filter, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

// services
import { SessionManagementService } from './services/session-management.service';
import { NavigationService, chromeVisibilityForUrl } from './services/navigation.service';
import { RailStateService } from './navigation/shell-rail/rail-state.service';
import { ThemeService } from './services/theme.service';
import { TranslateService } from '@ngx-translate/core';

import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  /** Must match the `.topbar` height in shell-topbar.component.scss. */
  static readonly TOPBAR_HEIGHT = 44;

  readonly title = 'the-amp-app-v1';
  private currentTitleKey: string | null = null;

  // Seeded from the boot URL (NBO-2343): on a cold new-tab open of a rail-less
  // route the corrective NavigationEnd is stalled behind auth guards + lazy
  // chunks, and a hardcoded everything-visible seed would paint the rail over
  // the editor for that whole window. Two flags since NBO-2207: the editor
  // hides only the rail and keeps the top bar (decision 2026-09-02).
  readonly railHidden = signal(chromeVisibilityForUrl(window.location.pathname).hideRail);
  readonly topbarHidden = signal(chromeVisibilityForUrl(window.location.pathname).hideTopbar);

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private sessionManagementService: SessionManagementService,
    private navigationService: NavigationService,
    private railState: RailStateService,
    // Injected for its construction side effect: ThemeService applies the saved
    // theme in its constructor, and it is otherwise only reached from the
    // top-bar user menu — which never instantiates on a chromeless route
    // (NBO-2343), so a dark-mode user opening an editor directly would get a
    // light shell. AppComponent is always constructed.
    themeService: ThemeService,
    private translate: TranslateService,
    private destroyRef: DestroyRef
  ) {
    // Publish the live chrome insets so viewport-fixed overlays can clear them
    // — .full-screen-edit (~25 detail screens) is position: fixed and used to
    // hardcode the old 56px icon rail. The height token matters for the same
    // reason the width one does: those overlays pin `top: 0`, which put them
    // straight over the new top bar (reported live on detail pages).
    this.applyCanvas(chromeVisibilityForUrl(window.location.pathname).canvas);

    effect(() => {
      const width = this.railHidden()
        ? 0
        : this.railState.collapsed()
          ? RailStateService.WIDTH_COLLAPSED
          : RailStateService.WIDTH_EXPANDED;
      const root = document.documentElement.style;
      root.setProperty('--app-rail-width', `${width}px`);
      root.setProperty(
        '--app-topbar-height',
        this.topbarHidden() ? '0px' : `${AppComponent.TOPBAR_HEIGHT}px`
      );
    });
  }

  ngOnInit(): void {
    this.initializeApplication();
    this.setupPageTitleHandling();

    // NavigationService owns chrome-visibility tracking (NBO-2343).
    this.navigationService.navigationState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.railHidden.set(state.hideRail);
        this.topbarHidden.set(state.hideTopbar);
        this.applyCanvas(state.canvas);
      });
  }

  /**
   * A route that adopted the new shell renders on the grey canvas with no
   * shell inset (NBO-2390). Expressed as a body class, exactly like
   * `auth-route`: the two rules live in _layout.scss at 0-2-1, which beats
   * this component’s own emulated 0-2-0 `.app-scroll-region` /
   * `.app-content` rules regardless of order. Seeded from the boot URL in
   * the constructor for the same cold-load reason the chrome flags are.
   */
  private applyCanvas(canvas: boolean): void {
    document.body.classList.toggle('shell-canvas', canvas);
  }

  /**
   * Initialize essential application services
   */
  private initializeApplication(): void {
    if (!environment.production) {
      (<any>window).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }
  }

  /**
   * Set up dynamic page title handling based on route data.
   * Sidebar visibility is tracked by NavigationService itself (NBO-2343).
   */
  private setupPageTitleHandling(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let child = this.activatedRoute.firstChild;

          while (child) {
            if (child.firstChild) {
              child = child.firstChild;
            } else if (child.snapshot.data && child.snapshot.data['title']) {
              return child.snapshot.data['title'];
            } else {
              return null;
            }
          }
          return null;
        }),
        switchMap((titleKey: string | null) => {
          this.currentTitleKey = titleKey;
          return titleKey ? this.translate.get(titleKey) : of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((title: string | null) => {
        if (title) {
          this.titleService.setTitle(title);
        }
      });

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.currentTitleKey) {
          this.titleService.setTitle(this.translate.instant(this.currentTitleKey));
        }
      });

    this.translate.onTranslationChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.currentTitleKey) {
          this.titleService.setTitle(this.translate.instant(this.currentTitleKey));
        }
      });
  }

  ngOnDestroy(): void {
    // Stop session management
    this.sessionManagementService.stopActivityTracking();
  }
}
